import { useEffect, useRef, useState, useMemo } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import { api } from "@/lib/api";
import { STATUS_COLORS, CLEANING_RING, REFERENTS, PLATFORM_META } from "@/lib/constants";
import { useCleaningCompanies } from "@/lib/useCleaningCompanies";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, X, SlidersHorizontal } from "lucide-react";
import MapAptCard from "@/components/MapAptCard";
import MapSiteCard from "@/components/MapSiteCard";
import MapLegend from "@/components/MapLegend";
import { aptPinSvg, sitePinSvg } from "@/lib/pin";
import GoogleAptMap from "@/components/GoogleAptMap";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const GMAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function makeIcon(a) {
  return L.divIcon({
    className: "apt-marker",
    iconSize: [40, 54],
    iconAnchor: [20, 52],
    html: aptPinSvg(a, a.id),
  });
}

const FILTER_STATUSES = [
  { key: "ok", label: "Aucune anomalie" },
  { key: "minor", label: "Mineure" },
  { key: "major", label: "Majeure" },
  { key: "critical", label: "Critique" },
];

const emptyFilters = {
  status: [], cleaning: [], referent: [], platforms: [], missing_platforms: [], loc: [],
  wifi: false, ac: false, mobility: false, elevator: false,
  capacity: "", bedrooms: "", sleepings: "", floor: "",
  show_hq: true, show_offices: true,
};

function locKey(...parts) {
  return [parts[0] || "", parts[1] || "", parts[2] || "", parts[3] || ""].join("|");
}

function FilterPanel({ filters, setFilters, count, hierarchy, companyFilterOptions, onReset, onClose }) {
  const toggle = (key, val) => {
    setFilters((f) => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(val) ? arr.filter((v) => v !== val) : [...arr, val] };
    });
  };
  const bool = (key) => (v) => setFilters((f) => ({ ...f, [key]: !!v }));
  const num = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));

  const Check = ({ checked, onChange, label, dot, testid }) => (
    <label className="flex items-center gap-2 text-sm cursor-pointer py-0.5">
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testid} />
      {dot && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: dot }} />}
      <span className="text-slate-700">{label}</span>
    </label>
  );

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <h3 className="font-bold font-display text-slate-900">Filtres</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500" data-testid="apt-count">{count} appartements</span>
          <button onClick={onClose} data-testid="close-filters" className="p-1.5 rounded-lg hover:bg-slate-100">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto thin-scroll px-4 py-3">
        <Accordion type="multiple" defaultValue={["etat"]} className="space-y-1">

          <AccordionItem value="etat" className="border border-slate-100 rounded-xl px-3">
            <AccordionTrigger className="text-sm font-semibold text-slate-800 py-3 hover:no-underline" data-testid="filter-group-etat">État</AccordionTrigger>
            <AccordionContent className="pb-3 space-y-1">
              {FILTER_STATUSES.map((s) => (
                <Check key={s.key} checked={filters.status.includes(s.key)} onChange={() => toggle("status", s.key)}
                       label={s.label} dot={STATUS_COLORS[s.key].hex} testid={`filter-status-${s.key}`} />
              ))}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="loc" className="border border-slate-100 rounded-xl px-3">
            <AccordionTrigger className="text-sm font-semibold text-slate-800 py-3 hover:no-underline" data-testid="filter-group-loc">Localisation</AccordionTrigger>
            <AccordionContent className="pb-3">
              {Object.entries(hierarchy).map(([country, regions]) => (
                <div key={country} className="mb-2">
                  <Check checked={filters.loc.includes(locKey(country))} onChange={() => toggle("loc", locKey(country))}
                         label={country} testid={`filter-loc-${country}`} />
                  <div className="ml-6 mt-1 space-y-1 border-l border-slate-100 pl-3">
                    {Object.entries(regions).map(([region, depts]) => (
                      <div key={region}>
                        <Check checked={filters.loc.includes(locKey(country, region))}
                               onChange={() => toggle("loc", locKey(country, region))}
                               label={region} testid={`filter-loc-${region}`} />
                        <div className="ml-5 mt-1 space-y-1 border-l border-slate-100 pl-3">
                          {Object.entries(depts).map(([dept, arrs]) => (
                            <div key={dept}>
                              <Check checked={filters.loc.includes(locKey(country, region, dept))}
                                     onChange={() => toggle("loc", locKey(country, region, dept))}
                                     label={dept} testid={`filter-loc-${dept}`} />
                              {arrs.size > 0 && (
                                <div className="ml-5 mt-1 flex flex-wrap gap-1.5 pb-1">
                                  {[...arrs].sort().map((arr) => {
                                    const k = locKey(country, region, dept, arr);
                                    const on = filters.loc.includes(k);
                                    return (
                                      <button key={arr} onClick={() => toggle("loc", k)} data-testid={`filter-arr-${arr}`}
                                        className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                                          on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
                                        }`}>{arr}</button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="menage" className="border border-slate-100 rounded-xl px-3">
            <AccordionTrigger className="text-sm font-semibold text-slate-800 py-3 hover:no-underline" data-testid="filter-group-menage">Ménage</AccordionTrigger>
            <AccordionContent className="pb-3 space-y-1">
              {companyFilterOptions.map((c) => (
                <Check key={c} checked={filters.cleaning.includes(c)} onChange={() => toggle("cleaning", c)}
                       label={c} dot={CLEANING_RING[c]?.hex} testid={`filter-cleaning-${c}`} />
              ))}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="location" className="border border-slate-100 rounded-xl px-3">
            <AccordionTrigger className="text-sm font-semibold text-slate-800 py-3 hover:no-underline" data-testid="filter-group-location">Location</AccordionTrigger>
            <AccordionContent className="pb-3 space-y-1">
              <Check checked={filters.mobility} onChange={bool("mobility")} label="Bail mobilité" testid="filter-mobility" />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="sites" className="border border-slate-100 rounded-xl px-3">
            <AccordionTrigger className="text-sm font-semibold text-slate-800 py-3 hover:no-underline" data-testid="filter-group-sites">Points remarquables</AccordionTrigger>
            <AccordionContent className="pb-3 space-y-1">
              <Check checked={filters.show_hq} onChange={bool("show_hq")} label="QG (bureau général)" testid="filter-show-hq" />
              <Check checked={filters.show_offices} onChange={bool("show_offices")} label="Bureaux de ménage" testid="filter-show-offices" />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="equip" className="border border-slate-100 rounded-xl px-3">
            <AccordionTrigger className="text-sm font-semibold text-slate-800 py-3 hover:no-underline" data-testid="filter-group-equip">Équipements</AccordionTrigger>
            <AccordionContent className="pb-3 space-y-1">
              <Check checked={filters.wifi} onChange={bool("wifi")} label="Wi-Fi" testid="filter-wifi" />
              <Check checked={filters.ac} onChange={bool("ac")} label="Climatisation" testid="filter-ac" />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="apt" className="border border-slate-100 rounded-xl px-3">
            <AccordionTrigger className="text-sm font-semibold text-slate-800 py-3 hover:no-underline" data-testid="filter-group-apt">Appartement</AccordionTrigger>
            <AccordionContent className="pb-3 space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[11px] text-slate-500 mb-1">Voyageurs ≥</div>
                  <Input type="number" min="0" value={filters.capacity} onChange={num("capacity")} className="h-8" data-testid="filter-capacity" />
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 mb-1">Chambres ≥</div>
                  <Input type="number" min="0" value={filters.bedrooms} onChange={num("bedrooms")} className="h-8" data-testid="filter-bedrooms" />
                </div>
                <div>
                  <div className="text-[11px] text-slate-500 mb-1">Couchages ≥</div>
                  <Input type="number" min="0" value={filters.sleepings} onChange={num("sleepings")} className="h-8" data-testid="filter-sleepings" />
                </div>
              </div>
              <Check checked={filters.elevator} onChange={bool("elevator")} label="Ascenseur" testid="filter-elevator" />
              <div>
                <div className="text-[11px] text-slate-500 mb-1">Étage</div>
                <Input value={filters.floor} onChange={num("floor")} placeholder="Ex : 3e" className="h-8" data-testid="filter-floor" />
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="ref" className="border border-slate-100 rounded-xl px-3">
            <AccordionTrigger className="text-sm font-semibold text-slate-800 py-3 hover:no-underline" data-testid="filter-group-ref">Référent</AccordionTrigger>
            <AccordionContent className="pb-3 space-y-1">
              {REFERENTS.map((r) => (
                <Check key={r} checked={filters.referent.includes(r)} onChange={() => toggle("referent", r)}
                       label={r} testid={`filter-ref-${r}`} />
              ))}
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="plat" className="border border-slate-100 rounded-xl px-3">
            <AccordionTrigger className="text-sm font-semibold text-slate-800 py-3 hover:no-underline" data-testid="filter-group-plat">Plateformes</AccordionTrigger>
            <AccordionContent className="pb-3 space-y-1">
              {Object.entries(PLATFORM_META).map(([k, m]) => (
                <Check key={k} checked={filters.platforms.includes(k)} onChange={() => toggle("platforms", k)}
                       label={m.name} dot={m.color} testid={`filter-platform-${k}`} />
              ))}
              <div className="pt-2 mt-2 border-t border-slate-100 space-y-1">
                <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1">Annonces manquantes</div>
                {Object.entries(PLATFORM_META).map(([k, m]) => (
                  <Check key={`no-${k}`} checked={filters.missing_platforms.includes(k)} onChange={() => toggle("missing_platforms", k)}
                         label={`Sans ${m.name}`} dot={m.color} testid={`filter-missing-${k}`} />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
      <div className="p-3 border-t border-slate-100">
        <Button variant="outline" onClick={onReset} data-testid="reset-filters" className="w-full">
          Réinitialiser les filtres
        </Button>
      </div>
    </div>
  );
}

export default function MapPage() {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const clusterRef = useRef(null);
  const [apartments, setApartments] = useState([]);
  const [sites, setSites] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [mapZoom, setMapZoom] = useState(6);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(emptyFilters);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { names: cleaningCompanyNames } = useCleaningCompanies();
  const companyFilterOptions = [...new Set([...cleaningCompanyNames, "Non renseigné"])];

  useEffect(() => {
    api.get("/apartments").then((r) => setApartments(r.data)).catch(() => {});
    api.get("/sites").then((r) => setSites(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent("apt-panel-toggle", { detail: !!selected }));
    return () => window.dispatchEvent(new CustomEvent("apt-panel-toggle", { detail: false }));
  }, [selected]);

  const hierarchy = useMemo(() => {
    const h = {};
    apartments.forEach((a) => {
      const c = a.country || "France";
      const r = a.region || "Île-de-France";
      const d = a.department || (a.city === "Paris" ? "Paris (75)" : "Autre");
      h[c] = h[c] || {};
      h[c][r] = h[c][r] || {};
      h[c][r][d] = h[c][r][d] || new Set();
      if (a.arrondissement) h[c][r][d].add(a.arrondissement);
    });
    return h;
  }, [apartments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return apartments.filter((a) => {
      if (q) {
        const hay = `${a.name} ${a.address} ${a.postal_code} ${a.owner_name || ""} ${a.arrondissement || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (filters.status.length && !filters.status.includes(a.color_status)) return false;
      if (filters.cleaning.length && !filters.cleaning.includes(a.cleaning_company)) return false;
      if (filters.referent.length && !filters.referent.includes(a.referent)) return false;
      if (filters.platforms.length) {
        const plats = (a.platforms || []).map((p) => p.platform);
        if (!filters.platforms.every((p) => plats.includes(p))) return false;
      }
      if (filters.missing_platforms.length) {
        const plats = (a.platforms || []).map((p) => p.platform);
        if (!filters.missing_platforms.some((p) => !plats.includes(p))) return false;
      }
      if (filters.loc.length) {
        const path = [a.country || "France", a.region || "", a.department || "", a.arrondissement || ""];
        const ok = filters.loc.some((key) =>
          key.split("|").every((part, i) => part === "" || part === path[i])
        );
        if (!ok) return false;
      }
      if (filters.wifi && !a.wifi_available) return false;
      if (filters.ac && !a.ac_available) return false;
      if (filters.mobility && !a.mobility_lease) return false;
      if (filters.elevator && !a.elevator) return false;
      if (filters.capacity && (a.max_guests || 0) < parseInt(filters.capacity)) return false;
      if (filters.bedrooms && (a.bedrooms || 0) < parseInt(filters.bedrooms)) return false;
      if (filters.sleepings && (a.sleepings || 0) < parseInt(filters.sleepings)) return false;
      if (filters.floor && String(a.floor || "").toLowerCase() !== filters.floor.trim().toLowerCase()) return false;
      return true;
    });
  }, [apartments, search, filters]);

  useEffect(() => {
    if (GMAPS_KEY || mapRef.current || !mapEl.current) return;
    const map = L.map(mapEl.current, { zoomControl: false }).setView([48.8566, 2.3522], 12);
    L.control.zoom({ position: "bottomright" }).addTo(map);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "&copy; OpenStreetMap",
      maxZoom: 19,
    }).addTo(map);
    const cluster = L.markerClusterGroup({ maxClusterRadius: 45 });
    map.addLayer(cluster);
    map.on("zoomend", () => setMapZoom(map.getZoom()));
    mapRef.current = map;
    clusterRef.current = cluster;
  }, []);

  useEffect(() => {
    if (GMAPS_KEY) return;
    const cluster = clusterRef.current;
    if (!cluster) return;
    cluster.clearLayers();
    filtered.forEach((a) => {
      const m = L.marker([a.latitude, a.longitude], {
        icon: makeIcon(a),
      });
      m.on("click", () => { setSelectedSite(null); setSelected(a); });
      m.bindTooltip(a.name, { direction: "top", offset: [0, -48] });
      cluster.addLayer(m);
    });
    sites
      .filter((s) => s.latitude && s.longitude && (s.type === "hq" ? filters.show_hq : filters.show_offices))
      .forEach((s) => {
        const m = L.marker([s.latitude, s.longitude], {
          icon: L.divIcon({ className: "apt-marker", iconSize: [40, 54], iconAnchor: [20, 52], html: sitePinSvg(s) }),
        });
        m.on("click", () => { setSelected(null); setSelectedSite(s); });
        m.bindTooltip(s.name, { direction: "top", offset: [0, -48] });
        cluster.addLayer(m);
      });
  }, [filtered, sites, filters.show_hq, filters.show_offices]);

  const activeBadges = useMemo(() => {
    const b = [];
    const t = (key, val) => setFilters((f) => ({ ...f, [key]: f[key].filter((v) => v !== val) }));
    filters.status.forEach((s) => b.push({ key: `s-${s}`, label: STATUS_COLORS[s].label, remove: () => t("status", s) }));
    filters.cleaning.forEach((c) => b.push({ key: `c-${c}`, label: c, remove: () => t("cleaning", c) }));
    filters.referent.forEach((r) => b.push({ key: `r-${r}`, label: r, remove: () => t("referent", r) }));
    filters.platforms.forEach((p) => b.push({ key: `p-${p}`, label: PLATFORM_META[p]?.name || p, remove: () => t("platforms", p) }));
    filters.missing_platforms.forEach((p) => b.push({ key: `mp-${p}`, label: `Sans ${PLATFORM_META[p]?.name || p}`, remove: () => t("missing_platforms", p) }));
    filters.loc.forEach((l) => b.push({ key: `l-${l}`, label: l.split("|").filter(Boolean).pop(), remove: () => t("loc", l) }));
    if (filters.wifi) b.push({ key: "wifi", label: "Wi-Fi", remove: () => setFilters((f) => ({ ...f, wifi: false })) });
    if (filters.ac) b.push({ key: "ac", label: "Climatisation", remove: () => setFilters((f) => ({ ...f, ac: false })) });
    if (filters.mobility) b.push({ key: "mob", label: "Bail mobilité", remove: () => setFilters((f) => ({ ...f, mobility: false })) });
    if (filters.elevator) b.push({ key: "elev", label: "Ascenseur", remove: () => setFilters((f) => ({ ...f, elevator: false })) });
    if (filters.capacity) b.push({ key: "cap", label: `≥ ${filters.capacity} voyageurs`, remove: () => setFilters((f) => ({ ...f, capacity: "" })) });
    if (filters.bedrooms) b.push({ key: "bed", label: `≥ ${filters.bedrooms} chambres`, remove: () => setFilters((f) => ({ ...f, bedrooms: "" })) });
    if (filters.sleepings) b.push({ key: "slp", label: `≥ ${filters.sleepings} couchages`, remove: () => setFilters((f) => ({ ...f, sleepings: "" })) });
    if (filters.floor) b.push({ key: "flr", label: `Étage ${filters.floor}`, remove: () => setFilters((f) => ({ ...f, floor: "" })) });
    return b;
  }, [filters]);

  return (
    <div className="h-[calc(100vh-3.5rem)] md:h-screen relative overflow-hidden">
      <div className="relative h-full flex">
        {/* Map */}
        <div className="relative flex-1">
          <div className={`absolute top-3 left-3 right-16 z-[1000] flex items-center gap-2 transition-all duration-300 ${filtersOpen ? "opacity-0 -translate-y-2 pointer-events-none" : "opacity-100 translate-y-0"}`}>
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-1.5 flex items-center justify-center flex-shrink-0" data-testid="map-home-logo">
              <img src="/logo.png" alt="HSH Ops" className="w-8 h-8 object-contain rounded-lg" />
            </div>
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 flex items-center px-3">
              <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                     placeholder="Rechercher adresse, nom ou propriétaire…" data-testid="map-search"
                     className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 shadow-none" />
              {search && <button onClick={() => setSearch("")}><X className="w-4 h-4 text-slate-400" /></button>}
            </div>
          </div>
          <div className={`absolute top-3 left-3 z-[1002] transition-all duration-300 ${filtersOpen ? "opacity-0 pointer-events-none" : "opacity-100"}`} style={{ top: "3.75rem" }}>
            <Button variant="outline" onClick={() => setFiltersOpen(true)} data-testid="open-filters-btn"
                    className="bg-white shadow-sm">
              <SlidersHorizontal className="w-4 h-4 mr-1.5" />Filtres
              {activeBadges.length > 0 && (
                <span className="ml-1.5 w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] flex items-center justify-center">{activeBadges.length}</span>
              )}
            </Button>
          </div>

          {activeBadges.length > 0 && !filtersOpen && (
            <div className="absolute top-[7.5rem] left-3 right-3 z-[999] flex flex-wrap gap-1.5" data-testid="active-filters">
              {activeBadges.map((b) => (
                <Badge key={b.key} onClick={b.remove}
                       className="bg-white text-slate-800 border border-slate-200 cursor-pointer hover:bg-slate-50">
                  {b.label} <X className="w-3 h-3 ml-1" />
                </Badge>
              ))}
              <Badge className="bg-slate-900 text-white">{filtered.length} appartements trouvés</Badge>
            </div>
          )}

          {GMAPS_KEY ? (
            <GoogleAptMap points={filtered.filter((a) => a.latitude && a.longitude)}
                          sites={sites.filter((s) => s.latitude && s.longitude && (s.type === "hq" ? filters.show_hq : filters.show_offices))}
                          onSelect={(a) => { setSelectedSite(null); setSelected(a); }}
                          onSelectSite={(s) => { setSelected(null); setSelectedSite(s); }}
                          onZoomChange={setMapZoom} />
          ) : (
            <div ref={mapEl} className="absolute inset-0 z-0" data-testid="leaflet-map" />
          )}
          {selected && <MapAptCard apt={selected} onClose={() => setSelected(null)} />}
          {selectedSite && <MapSiteCard site={selectedSite} onClose={() => setSelectedSite(null)} />}
          <MapLegend globalView={mapZoom < 10} />
        </div>
      </div>

      {/* Filter drawer — overlays search bar */}
      <div className={`absolute inset-y-0 left-0 w-80 max-w-[88vw] border-r border-slate-200 shadow-2xl z-[1003] transition-transform duration-300 ${filtersOpen ? "translate-x-0" : "-translate-x-full"}`}
           data-testid="filters-drawer">
        <FilterPanel filters={filters} setFilters={setFilters} count={filtered.length}
                     hierarchy={hierarchy} companyFilterOptions={companyFilterOptions}
                     onReset={() => setFilters(emptyFilters)}
                     onClose={() => setFiltersOpen(false)} />
      </div>

    </div>
  );
}
